import PropTypes from "prop-types";
import { StyledDecisionCardProperties, StyledDecisionCardTags } from "../../../styles/StyledDecisionCard";
import { StyledDecisionCard } from "../../../styles/StyledDecisionCard/StyledDecisionCard";
import { Tag } from "../../Tag";
import { StyledLink } from "../../../styles/Link";
import { Button } from "../../Button";
import { useAppContext } from "../../../context/appContext";
import moment from "moment";
import convertStringToColor from "../../../helpers/util/convertStringToColor";
import { useEffect, useState } from "react";
import TreeIcon from "../../../assets/img/icons/tree.svg";
import DecisionTreeModal from "../../DecisionTreeModal";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import { getDecisionsList, getDecision } from "../../../api/decision/decision";
import { getDirectionsList } from "../../../api/decision/directions";
import { getOverviewTab } from "../../../api/decision/tabs";
import { ActiveIcon, PendingIcon, ExpiredIcon, OngoingIcon, NoValidityIcon } from "../../StatusIcons/StatusIcons";

export default function DecisionCard({ data, openModal, clearSearchResults, decisionsList }) {
	const { items } = useAppContext();
	const { axiosPrivate } = useAxiosPrivate();
	const [strippedShortBasis, setStrippedShortBasis] = useState("");
	const [showDecisionTree, setShowDecisionTree] = useState(false);
	const [allDecisions, setAllDecisions] = useState([]);
	const [fullDecisionData, setFullDecisionData] = useState(null);
	const [overviewTabData, setOverviewTabData] = useState(null);
	const [directionsData, setDirectionsData] = useState([]);
	const [isLoadingDecisions, setIsLoadingDecisions] = useState(false);
	const [incomingRelations, setIncomingRelations] = useState([]);
	const [decisionReceivers, setDecisionReceivers] = useState([]);
	
	// Use fullDecisionData if available, otherwise fall back to basic data
	// Include overview tab data for basis content
	const currentData = fullDecisionData 
		? { 
			...fullDecisionData, 
			// Get basis content from overview tab data
			shortBasis: overviewTabData?.basisDescription || fullDecisionData.shortBasis || fullDecisionData.basis || data.shortBasis || data.basis,
			shortDecision: overviewTabData?.decisionDescription || fullDecisionData.shortDecision || data.shortDecision,
			basisDescription: overviewTabData?.basisDescription,
			decisionDescription: overviewTabData?.decisionDescription
		}
		: { 
			...data,
			// For initial data, also try to use any overview data if available
			shortBasis: overviewTabData?.basisDescription || data.shortBasis || data.basis,
			shortDecision: overviewTabData?.decisionDescription || data.shortDecision
		};
		
	const { id, name, label, validFrom, validTo, decisionDate, shortBasis, shortDecision, keywords, unit: unitItem } = currentData;
	const unit = currentData.unit?.name || data.unit?.name || "Unknown Unit";
	const category = currentData.category?.name || data.category?.name || "Unknown Category";
	const labelName = label?.name || "Unknown Label";

	// Fetch full decision data and overview tab on component mount
	useEffect(() => {
		const fetchFullDecisionData = async () => {
			try {
				// Fetch basic decision data
				const fullData = await getDecision(axiosPrivate, id);
				
				// Fetch overview tab data (contains basis and decision descriptions)
				const overviewData = await getOverviewTab(axiosPrivate, id);
				
				if (fullData) {
					setFullDecisionData(fullData);
				}
				
				if (overviewData) {
					setOverviewTabData(overviewData);
				}
			} catch (error) {
				console.error("Error fetching decision data:", error);
			}
		};

		fetchFullDecisionData();
	}, [id, axiosPrivate]);

	// Use decisionDate if available, otherwise fall back to validFrom
	const displayDate = decisionDate || validFrom;

	// Format dates for display - handle ISO strings properly (shorter format for cards)
	const formatDate = (date) => {
		if (!date) return null;
		
		// Handle ISO format dates (like "2025-08-08T00:00:00+00:00")
		const momentDate = moment(date);
		return momentDate.isValid() ? momentDate.format("MMM DD, YYYY") : null;
	};

	// More compact date format for validity period to help fit on one line
	const formatDateCompact = (date) => {
		if (!date) return null;
		
		const momentDate = moment(date);
		return momentDate.isValid() ? momentDate.format("MMM DD, YY") : null;
	};

	// Get validity status with modern icon component
	const getValidityStatus = () => {
		const now = moment();
		const validFromDate = validFrom ? moment(validFrom) : null;
		const validToDate = validTo ? moment(validTo) : null;

		if (validFromDate && validToDate) {
			if (now.isBefore(validFromDate)) {
				return { status: "pending", icon: <PendingIcon />, color: "#f59e0b" };
			} else if (now.isAfter(validToDate)) {
				return { status: "expired", icon: <ExpiredIcon />, color: "#ef4444" };
			} else {
				return { status: "active", icon: <ActiveIcon />, color: "#10b981" };
			}
		} else if (validFromDate) {
			if (now.isBefore(validFromDate)) {
				return { status: "pending", icon: <PendingIcon />, color: "#f59e0b" };
			} else {
				return { status: "ongoing", icon: <OngoingIcon />, color: "#3b82f6" };
			}
		} else if (validToDate) {
			if (now.isAfter(validToDate)) {
				return { status: "expired", icon: <ExpiredIcon />, color: "#ef4444" };
			} else {
				return { status: "active", icon: <ActiveIcon />, color: "#10b981" };
			}
		}
		return { status: "ongoing", icon: <NoValidityIcon />, color: "#6b7280" };
	};

	// Create validity period string with status
	const getValidityPeriod = () => {
		const validFromFormatted = formatDateCompact(validFrom);
		const validToFormatted = formatDateCompact(validTo);
		const statusInfo = getValidityStatus();
		
		let dateRange = "";
		if (validFromFormatted && validToFormatted) {
			dateRange = `${validFromFormatted} - ${validToFormatted}`;
		} else if (validFromFormatted) {
			dateRange = `From ${validFromFormatted}`;
		} else if (validToFormatted) {
			dateRange = `Until ${validToFormatted}`;
		} else {
			dateRange = "No period set";
		}

		return {
			text: dateRange,
			status: statusInfo
		};
	};

	// Fetch all decisions when tree modal is opened
	const handleOpenDecisionTree = async () => {
		setIsLoadingDecisions(true);
		setShowDecisionTree(true);
		setIncomingRelations([]);
		setDecisionReceivers([]);
		
		try {
			// Fetch the complete decision details
			const fullDecisionData = await getDecision(axiosPrivate, id);
			if (fullDecisionData) {
				setFullDecisionData(fullDecisionData);
			}

			// Fetch directions data for current decision
			const directions = await getDirectionsList(axiosPrivate, id);
			if (Array.isArray(directions)) {
				setDirectionsData(directions);
			} else {
				setDirectionsData([]);
			}

			// Fetch decision receivers using the correct API endpoint
			try {
				const receiversResponse = await axiosPrivate.get(`/decision/${id}/organization-unit.json`);
				if (receiversResponse.data && Array.isArray(receiversResponse.data)) {
					setDecisionReceivers(receiversResponse.data);
				} else {
					setDecisionReceivers([]);
				}
			} catch (receiversError) {
				console.log("⚠️ No receivers found or error fetching receivers:", receiversError.message);
				setDecisionReceivers([]);
			}
			
			// Fetch all other decisions and compute incoming relations
			const decisions = await getDecisionsList(axiosPrivate, id);
			if (Array.isArray(decisions)) {
				try {
					const fetches = decisions.map((d) =>
						getDirectionsList(axiosPrivate, d.id)
							.then((dirs) => {
								return { decision: d, dirs: Array.isArray(dirs) ? dirs : [] };
							})
							.catch(() => ({ decision: d, dirs: [] }))
					);
					const results = await Promise.allSettled(fetches);
					const incoming = [];
					const enrichedDecisions = [];
					
					results.forEach((res) => {
						if (res.status === "fulfilled") {
							const { decision: src, dirs } = res.value;
							
							// Add directions to the decision object for recursive processing
							const enrichedDecision = { ...src, directions: dirs };
							enrichedDecisions.push(enrichedDecision);
							
							// Check for incoming relations
							dirs.forEach((dir) => {
								const fd = dir.followDecision;
								const targetId = fd && (typeof fd === "object" ? fd.id : fd);
								if (targetId === id) {
									incoming.push({ fromDecision: src, direction: dir });
								}
							});
						}
					});
					
					setAllDecisions(enrichedDecisions);
					setIncomingRelations(incoming);
				} catch {
					setAllDecisions(decisions); // fallback to original decisions without directions
					setIncomingRelations([]);
				}
			} else {
				setAllDecisions([]);
			}
		} catch {
			setAllDecisions([]);
			setIncomingRelations([]);
		} finally {
			setIsLoadingDecisions(false);
		}
	};

	useEffect(() => {
		// Use shortBasis (the basis content) if available, otherwise fall back to shortDecision
		const shortBasisValue = currentData?.shortBasis || currentData?.basis;
		const fallbackContent = currentData?.shortDecision;
		
		const contentToDisplay = shortBasisValue || fallbackContent;
		
		if (contentToDisplay) {
			let htmlStr = contentToDisplay.replace(/<[^>]+>/g, "");
			const tempStr = htmlStr.replaceAll("&nbsp;", " ");

			setStrippedShortBasis(tempStr);
		} else {
			setStrippedShortBasis("");
		}
	}, [currentData, overviewTabData]);

	let acronym = "";
	if (unitItem && unitItem.name) {
		if (unitItem.name.split(" ").length > 1) {
			unitItem.name.split(" ").map((e, i) => {
				if (i < 2) acronym += e.split("")[0];
			});
		} else {
			unitItem.name.split("").map((e, i) => {
				if (i < 2) acronym += e;
			});
		}
	} else {
		// Fallback when unitItem is undefined - use unit string or default
		const fallbackUnit = unit || "Unknown";
		if (fallbackUnit.split(" ").length > 1) {
			fallbackUnit.split(" ").map((e, i) => {
				if (i < 2) acronym += e.split("")[0];
			});
		} else {
			fallbackUnit.split("").map((e, i) => {
				if (i < 2) acronym += e;
			});
		}
	}

	return (
		<>
			<style>
				{`
					@keyframes spin {
						from { transform: rotate(0deg); }
						to { transform: rotate(360deg); }
					}
				`}
			</style>
			<StyledDecisionCard>
				<div className="card-header">
					<div className="badges">
						<span className="badge badge-label">
							{labelName}
						</span>
						<span className="badge badge-category">
							{category}
						</span>
					</div>
					{Object.keys(items.userUnits).length !== 0 && unitItem && unitItem.id && items.userUnits[unitItem.id] && (
						<div className="card-actions">
							<button
								onClick={handleOpenDecisionTree}
								disabled={isLoadingDecisions}
								title="View Decision Tree"
							>
								<img 
									src={TreeIcon} 
									alt="Decision Tree" 
									style={{ 
										animation: isLoadingDecisions ? "spin 1s linear infinite" : "none"
									}} 
								/>
							</button>
							<StyledLink
								to={`/decisions/${id}`}
								onClick={() => {
									if (clearSearchResults) {
										localStorage.removeItem("search");
									}
									localStorage.setItem("decisionsList", JSON.stringify(decisionsList));
								}}
							>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15.033 15.033">
									<path
										d="M16.363,16.363H4.67V4.67h5.846V3H4.67A1.67,1.67,0,0,0,3,4.67V16.363a1.67,1.67,0,0,0,1.67,1.67H16.363a1.675,1.675,0,0,0,1.67-1.67V10.517h-1.67ZM12.187,3V4.67h3l-8.21,8.21,1.178,1.178,8.21-8.21v3h1.67V3Z"
										transform="translate(-3 -3)"
										fill="currentColor"
									/>
								</svg>
							</StyledLink>
						</div>
					)}
				</div>
				
				<div className="card-content">
					<h3>{name}</h3>
					{strippedShortBasis && <p className="description">{strippedShortBasis}</p>}
				<StyledDecisionCardProperties
					color={convertStringToColor(unitItem?.name || unit || "default")}
					basisDisplay={strippedShortBasis ? true : false}
					keywordDisplay={keywords && keywords.length > 0 ? true : false}
				>
					<div>
						<div className="icon">
							<span>{acronym}</span>
						</div>
						<span>Unit</span>
						<p>{unit}</p>
					</div>
					<div className="date-row">
						<div>
							<span>Decision Date</span>
							<p className="decision-date">
								{formatDate(decisionDate) || formatDate(displayDate) || "Not set"}
							</p>
						</div>
						<div>
							<span>Validity Period</span>
							<p className="validity-status">
								<span className="status-icon">
									{getValidityPeriod().status.icon}
								</span>
								<span>
									{getValidityPeriod().text}
								</span>
							</p>
						</div>
					</div>
				</StyledDecisionCardProperties>
				{keywords && keywords.length > 0 && (
					<StyledDecisionCardTags>
						{keywords.map((keyword, i) => (
							<Tag key={i}>{keyword}</Tag>
						))}
					</StyledDecisionCardTags>
				)}
				</div>
				
				<div className="card-footer">
					<span 
						className="preview-text"
						onClick={() => {
							openModal(id);
						}}
					>
						See preview
					</span>
				</div>
			</StyledDecisionCard>

			{/* Decision Tree Modal */}
			<DecisionTreeModal
				isOpen={showDecisionTree}
				onClose={() => setShowDecisionTree(false)}
				decisionData={fullDecisionData || data}
				directionsData={directionsData}
				allDecisions={allDecisions}
				incomingRelations={incomingRelations}
				decisionReceivers={decisionReceivers}
			/>
		</>
	);
}

DecisionCard.propTypes = {
	data: PropTypes.object,
	openModal: PropTypes.func,
	clearSearchResults: PropTypes.bool,
	decisionsList: PropTypes.array,
};
